import React, { useRef, useState } from 'react';

import Text from '../text';
import SearchIcon from '../icons/24/search';
import CloseIcon from '../icons/18/close';
import useInputValidation from '../use-input-validation';
import useClickOutside from '../hooks/use-click-outside';
import { useIsMounted } from '../hooks/use-is-mounted';

import {
  Search as Props,
  themes,
  inputSizes,
  variants,
  expandDirections,
} from './search.types';
import classNames from 'classnames';
import Button from '../button';

const RefSearch = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      button,
      customErrorMessages,
      clear,
      disabled,
      label,
      isLabelVisible = true,
      name,
      onChangeFunc = () => {},
      expandable,
      placeholder,
      required,
      inputSize = inputSizes.small,
      theme = themes.inline,
      variant = variants.default,
      value = '',
      helpText,
      helpIcon,
      ...restProps
    },
    ref,
  ) => {
    const [validationOnChange, onInvalid, error] =
      useInputValidation(customErrorMessages);
    const searchArea = useRef<HTMLDivElement>(null);
    const [expand, setExpand] = useState(false);
    const isMounted = useIsMounted();

    useClickOutside(searchArea, () => setExpand(false));

    const [inputValue, setInputValue] = useState(value);

    const onMessageChange = (message: string) => setInputValue(message);

    return (
      <div
        className={classNames('search', {
          [`search--expand-direction-${expandable?.direction}`]:
            expandable?.direction,
        })}
      >
        {isMounted ? (
          <div
            className={classNames('search__inner', {
              'search__inner--expandable': expandable,
              'search__inner--expand': expand,
            })}
            ref={searchArea}
          >
            {theme === themes.inline && isLabelVisible ? (
              <Text
                theme={Text.themes.emphasis}
                size={
                  inputSize === inputSizes.large
                    ? Text.sizes.large
                    : Text.sizes.small
                }
                className={classNames('search__header', {
                  [`search__header--variant-${variant}`]: variant,
                })}
              >
                {label}
              </Text>
            ) : null}
            <div
              className={classNames('search__input-wrapper', {
                [`search__input-wrapper--size-${inputSize}`]: inputSize,
                [`search__input-wrapper--theme-${theme}`]: theme,
                [`search__input-wrapper--variant-${variant}`]: variant,
              })}
            >
              <label className="search__label">
                <div
                  className={classNames('search__icon', {
                    [`search__icon--variant-${variant}`]: variant,
                  })}
                >
                  <SearchIcon />
                </div>
                <input
                  {...restProps}
                  className={classNames('search__input', {
                    [`search__input--variant-${variant}`]: variant,
                    [`search__input--size-${inputSize}`]: inputSize,
                  })}
                  disabled={disabled}
                  name={name}
                  onChange={e => {
                    onChangeFunc(e.target.value);
                    validationOnChange(e);
                    onMessageChange(e.target.value);
                  }}
                  onClick={() => setExpand(true)}
                  value={inputValue}
                  onInvalid={e => onInvalid(e)}
                  placeholder={placeholder}
                  ref={ref}
                  required={required}
                  type="search"
                  aria-label={
                    theme === themes.floating || !isLabelVisible
                      ? label
                      : undefined
                  }
                />
              </label>
              {inputValue ? (
                <Button
                  {...clear}
                  className="search__clear-button"
                  onClick={() => {
                    onChangeFunc('');
                    setInputValue('');
                  }}
                  type="button"
                  variant={
                    variant === variants.fixed
                      ? Button.variants.fixedTertiary
                      : Button.variants.tertiary
                  }
                  size={
                    inputSize === inputSizes.small
                      ? Button.sizes.small
                      : Button.sizes.basic
                  }
                />
              ) : null}
              {button ? (
                <Button
                  {...button}
                  className="search__search-button"
                  variant={
                    variant === variants.fixed
                      ? Button.variants.secondaryWhite
                      : Button.variants.secondary
                  }
                  type="submit"
                  size={
                    inputSize === inputSizes.small
                      ? Button.sizes.small
                      : Button.sizes.basic
                  }
                />
              ) : null}
              {expandable && expand ? (
                <Button
                  {...expandable.close}
                  className="search__close-button"
                  variant={
                    variant === variants.fixed
                      ? Button.variants.fixedTertiary
                      : Button.variants.tertiary
                  }
                  leadingIcon={<CloseIcon />}
                  onClick={() => {
                    setExpand(false);
                    onChangeFunc('');
                    setInputValue('');
                  }}
                  size={
                    inputSize === inputSizes.small
                      ? Button.sizes.small
                      : Button.sizes.basic
                  }
                />
              ) : null}
            </div>
            {error ? (
              <div aria-hidden="true" className="search__error">
                <Text>{error}</Text>
              </div>
            ) : null}
            {theme === themes.inline && helpText ? (
              <Text
                size={Text.sizes.interface}
                className={classNames('search__help-text', {
                  [`search__help-text--variant-${variant}`]: variant,
                })}
              >
                {helpIcon}
                {helpText}
              </Text>
            ) : null}
          </div>
        ) : (
          <div className="search__loading-inner">
            {theme === themes.inline && isLabelVisible ? (
              <div
                className={classNames('search__loading-extra', {
                  [`search__loading-extra--${variant}`]: variant,
                })}
              >
                {label}
              </div>
            ) : null}
            <div
              className={classNames('search__loading-input', {
                [`search__loading-input--${variant}`]: variant,
                [`search__loading-input--${inputSize}`]: inputSize,
              })}
            ></div>
            {theme === themes.inline && helpText ? (
              <div
                className={classNames('search__loading-extra', {
                  [`search__loading-extra--${variant}`]: variant,
                })}
              >
                {helpIcon}
                {helpText}
              </div>
            ) : null}
          </div>
        )}
      </div>
    );
  },
);

// NOTE: Stack traces will show "forwardRef" instead of "Search" if this is not set.
RefSearch.displayName = 'Search';

const Search: typeof RefSearch & {
  themes: typeof themes;
  variants: typeof variants;
  inputSizes: typeof inputSizes;
  expandDirections: typeof expandDirections;
} = Object.assign(RefSearch, {
  themes,
  variants,
  inputSizes,
  expandDirections,
});

export default Search;
